// src/components/Landing/LandingPage.jsx
// Drop this file into your project, then update routes in App.js as shown below.
// It uses only MUI + your existing theme. No extra deps.

import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import LanguageThemeBar from '../LanguageThemeBar';
import {alpha, useTheme} from '@mui/material/styles';
import {Link as RouterLink, useNavigate} from 'react-router-dom';
import {
    AppBar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    Divider,
    Grid,
    Alert,
    Snackbar,
    TextField,
    IconButton,
    Link,
    Stack,
    Toolbar,
    Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import LockIcon from '@mui/icons-material/Lock';
import {API_BASE} from "../../apiBase";

/**
 * CONFIG — tweak to your app
 */
const APP_NAME_SHORT = 'FloCo AI'; // collapsed format (e.g., acronym)
const APP_NAME_LONG = 'FlowCompanion AI'; // expanded format (full name)
const TAGLINE = 'Empowering Knowledge Workers.';
const CTA_TEXT = 'Try it free';
const CONTACT = {
    email: 'coralie.rohrer@bfh.ch',
    company: 'FloCo AI',
    addressLine1: 'Badenerstrasse 123',
    addressLine2: '8004 Zürich, Switzerland',
};

/**
 * Utility: small hook to know if a session token exists
 */
function useAuthToken() {
    const [token, setToken] = useState(() => localStorage.getItem('token'));
    useEffect(() => {
        const onStorage = () => setToken(localStorage.getItem('token'));
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);
    return token;
}

/**
 * TOP NAV
 */
function TopNav() {
    const {t} = useTranslation();
    const token = useAuthToken();

    return (
        <AppBar
            elevation={0}
            color="default"
            position="sticky"
            sx={(t) => ({
                backdropFilter: 'blur(6px)',
                backgroundColor: alpha(t.palette.background.default, 0.85),
                borderBottom: `1px solid ${t.palette.divider}`,
            })}
        > <Toolbar sx={{gap: 2}}>
            <Typography variant="h6" sx={{fontWeight: 800, letterSpacing: 0.5}}>
                {t('brand.short')}
            </Typography>
            <Box sx={{flexGrow: 1}}/>
            <Box sx={{mr: 2}}>
                <LanguageThemeBar/>
            </Box>
            {token ? (
                <Button component={RouterLink} to="/home" variant="contained" color="primary">
                    {t('nav.openApp')}
                </Button>
            ) : (
                <Stack direction="row" spacing={1}>
                    <Button component={RouterLink} to="/login" variant="text" color="secondary">
                        {t('nav.login')}
                    </Button> <Button
                    component={RouterLink}
                    to="/contact"
                    variant="contained"
                    color="primary"
                    endIcon={<ArrowForwardIcon/>}
                >
                    {t('nav.contact')}
                </Button>
                </Stack>
            )}
        </Toolbar>
        </AppBar>
    );
}

/**
 * HERO with collapsed→expanded brand on scroll
 */
function Hero() {
    const {t} = useTranslation();
    const token = useAuthToken();
    const [expanded, setExpanded] = useState(false);
    const theme = useTheme();

    useEffect(() => {
        const onScroll = () => setExpanded(window.scrollY > 80);
        onScroll();
        window.addEventListener("scroll", onScroll, {passive: true});
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <Box
            component="section"
            sx={{
                pt: {xs: 12, md: 16},
                pb: {xs: 12, md: 16},
                bgcolor: "background.default",
                color: "text.primary",
                backgroundImage: (t) => `
          radial-gradient(${alpha(t.palette.primary.main, 0.28)} 0%, transparent 60%),
          linear-gradient(0deg, ${alpha(t.palette.primary.main, 0.08)}, ${alpha(
                    t.palette.primary.main,
                    0.08
                )})
        `,
            }}
        >
            <Container maxWidth="lg">
                <Stack spacing={6} alignItems="center" textAlign="center">
                    {/* Title + tagline */}
                    <Box>
                        <Typography
                            variant="h1"
                            sx={{
                                fontSize: {xs: 64, md: 96},
                                fontWeight: 900,
                                lineHeight: 1.05,
                                transition: "all .35s ease",
                                letterSpacing: expanded ? 0.6 : 0.2,
                                opacity: 1,
                            }}
                        >
                            {expanded ? t("brand.long") : t("brand.short")}
                        </Typography>
                        <Typography
                            variant="h5"
                            sx={{
                                mt: {xs: 4, md: 6}, // 👈 more space between title and tagline
                                color: "text.secondary",
                            }}
                        >
                            {t("tagline")}
                        </Typography>
                    </Box>

                    {/* Buttons + video */}
                    <Stack
                        spacing={{xs: 6, md: 12}} // 👈 controls space between buttons and video
                        alignItems="center"
                        sx={{width: "100%"}}
                    >
                        {/* Button row */}
                        <Stack
                            direction={{xs: "column", sm: "row"}}
                            spacing={2}
                            sx={{mt: {xs: 4, md: 6}}} // 👈 extra space before buttons
                        >
                            <Button
                                component={RouterLink}
                                to="/contact"
                                size="large"
                                variant="contained"
                                color="primary"
                                endIcon={<ArrowForwardIcon/>}
                            >
                                {t("nav.contact")}
                            </Button>
                            {token ? (
                                <Button
                                    component={RouterLink}
                                    to="/home"
                                    size="large"
                                    variant="contained"
                                    color="primary"
                                >
                                    {t("nav.openApp")}
                                </Button>
                            ) : (
                                <Button
                                    component={RouterLink}
                                    to="/login"
                                    size="large"
                                    variant="outlined"
                                    color="secondary"
                                >
                                    {t("nav.login")}
                                </Button>
                            )}
                        </Stack>

                        {/* Video */}
                        <Box sx={{width: "100%"}}>
                            <Box
                                sx={{
                                    aspectRatio: "16/9",
                                    borderRadius: 2,
                                    overflow: "hidden",
                                    border: (t) => `1px solid ${alpha(t.palette.divider, 0.7)}`,
                                    boxShadow: (t) => t.shadows[2],
                                }}
                            >
                                <video
                                    src="/marketing_video_sound.mp4"
                                    autoPlay
                                    muted
                                    loop
                                    playsInline
                                    controls
                                    controlsList="nodownload"
                                    disablePictureInPicture
                                    style={{width: "100%", height: "100%", objectFit: "cover"}}
                                />
                            </Box>
                        </Box>
                    </Stack>
                </Stack>
            </Container>
        </Box>
    );
}


/**
 * FEATURES
 */
function Features() {
    const {t} = useTranslation();
    const items = useMemo(() => ([
        {title: t('features.upload.title'), desc: t('features.upload.desc')},
        {title: t('features.composable.title'), desc: t('features.composable.desc')},
        {title: t('features.local.title'), desc: t('features.local.desc')},
    ]), [t]);
    return (
        <Box component="section" sx={{py: {xs: 6, md: 10}, bgcolor: 'background.paper'}}>
            <Container maxWidth="lg">
                <Grid container spacing={3}>
                    {items.map((f, idx) => (
                        <Grid size={{xs: 12, md: 4}} key={idx}>
                            <Card
                                variant="outlined"
                                sx={(t) => ({
                                    height: '100%',
                                    backgroundColor: alpha(t.palette.background.default, 0.6),
                                    borderColor: alpha(t.palette.divider, 0.8),
                                })}
                            > <CardContent>
                                <Typography variant="h6" sx={{fontWeight: 800, mb: 1}}>{f.title}</Typography>
                                <Typography color="text.secondary">{f.desc}</Typography>
                            </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Container>
        </Box>
    );
}

/**
 * VIDEO TEASER (locked for guests, playable inside app for users)
 */
function TrainingVideosTeaser() {
    const {t, i18n} = useTranslation();
    const token = useAuthToken();

    // Reuse the same structure as VideoTutorials.js
    const videos = {
        en: [
            {
                title: "Playground Tutorial",
                file: "/tutorials/videos/playground_EN.mp4",
                thumb: "/tutorials/thumbnails/playground_EN.jpg",
            },
            // add more EN items here as you create them
        ],
        // NOTE: Your existing code uses "ge" (not "de") for German; keeping it consistent.
        ge: [
            {
                title: "Experimentierbereich-Tutorial",
                file: "/tutorials/videos/playground_DE.mp4",
                thumb: "/tutorials/thumbnails/playground_DE.jpg",
            },
        ],
        fr: [
            {
                title: "Tutoriel sur l'espace d'expérimentation",
                file: "/tutorials/videos/playground_FR.mp4",
                thumb: "/tutorials/thumbnails/playground_FR.jpg",
            },
        ],
    };

    const lang = i18n.language || "en";
    const langVideos = videos[lang] || videos.en;

    return (
        <Box
            component="section"
            sx={{
                py: {xs: 6, md: 10},
                bgcolor: "primary.main",
                color: "primary.contrastText",
                borderTop: (t) => `1px solid ${t.palette.divider}`,
            }}
        >
            <Container maxWidth="lg">
                <Grid container spacing={4} alignItems="center">
                    <Grid size={{xs: 12, md: 6}}>
                        <Typography variant="h4" sx={{fontWeight: 900, mb: 1, color: "inherit"}}>
                            {t("videos.title")}
                        </Typography>
                        <Typography sx={{mb: 2, color: "inherit", opacity: 0.9}}>
                            {t("videos.blurb")}
                        </Typography>

                        {token ? (
                            <Button
                                component={RouterLink}
                                to="/home#video-tutorials"
                                variant="contained"
                                color="info"
                                endIcon={<PlayCircleOutlineIcon/>}
                            >
                                {t("videos.openAcademy")}
                            </Button>
                        ) : (
                            <Stack direction={{xs: "column", sm: "row"}} spacing={1} alignItems="center">
                                <Chip icon={<LockIcon/>} label={t("videos.membersOnly")} color="info"/>
                            </Stack>
                        )}
                    </Grid>

                    {/*/!* Thumbnails grid only (compact) *!/*/}
                    {/*<Grid item xs={12} md={6}>*/}
                    {/*    <Grid container spacing={2}>*/}
                    {/*        {langVideos.slice(0, 6).map((video, idx) => (*/}
                    {/*            <Grid key={idx} item xs={6} sm={4}>*/}
                    {/*                <Box*/}
                    {/*                    component="img"*/}
                    {/*                    src={video.thumb}*/}
                    {/*                    alt={video.title}*/}
                    {/*                    sx={{*/}
                    {/*                        width: "100%",*/}
                    {/*                        height: 110,        // small, consistent height*/}
                    {/*                        borderRadius: 2,*/}
                    {/*                        objectFit: "cover",*/}
                    {/*                        border: (t) =>*/}
                    {/*                            `1px solid ${alpha(t.palette.primary.contrastText, 0.35)}`,*/}
                    {/*                    }}*/}
                    {/*                />*/}
                    {/*            </Grid>*/}
                    {/*        ))}*/}
                    {/*    </Grid>*/}
                    {/*</Grid>*/}
                </Grid>
            </Container>
        </Box>
    );
}


/**
 * FOOTER
 */
function Footer() {
    const {t} = useTranslation();
    return (
        <Box
            component="footer"
            sx={{
                pt: 8,
                pb: 6,
                bgcolor: "background.default",
                borderTop: (t) => `1px solid ${t.palette.divider}`,
            }}
        >
            <Container maxWidth="lg">
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        flexWrap: "wrap", // ensures responsiveness on small screens
                        gap: 2,
                    }}
                >
                    {/* Left: name + tagline */}
                    <Box>
                        <Typography
                            variant="h6"
                            sx={{fontWeight: 900, color: "text.primary"}}
                        >
                            {t('brand.short')}
                        </Typography>
                        <Typography color="text.secondary" sx={{mt: 1}}>
                            {t('tagline')}
                        </Typography>
                    </Box>

                    {/* Right: partner logo */}
                    <Box
                        component="img"
                        src="/partner-logo.png"
                        alt="Partner logo"
                        sx={{
                            height: 80,
                            maxWidth: "100%",
                            objectFit: "contain",
                        }}
                    />
                </Box>

                <Divider sx={{my: 3}}/>
                <Typography variant="body2" color="text.secondary">
                    {t('footer.copyright', {year: new Date().getFullYear(), company: CONTACT.company})}
                </Typography>
            </Container>

        </Box>
    );
}


/**
 * PAGE COMPOSITION
 */
export default function LandingPage() {
    return (
        <Box>
            <TopNav/>
            <Hero/>
            <Features/>
            <TrainingVideosTeaser/>
            <Footer/>
        </Box>
    );
}

// =========================
// GATED VIDEOS PAGE (for logged-in users)
// =========================
export function AcademyPage() {
    const token = useAuthToken();
    const navigate = useNavigate();

    useEffect(() => {
        if (!token) navigate('/login');
    }, [token, navigate]);

    // In production, fetch this list from your Flask API with the token.
    const videos = [
        {id: 'intro', title: 'Getting started', minutes: 2, src: ''},
        {id: 'build-tools', title: 'Create your first tool', minutes: 3, src: ''},
        {id: 'results', title: 'Understanding results', minutes: 2, src: ''},
    ];

    return (
        <Container maxWidth="md" sx={{py: 6}}>
            <Typography variant="h4" sx={{fontWeight: 900, mb: 3}}>Academy</Typography>
            <Grid container spacing={3}>
                {videos.map(v => (
                    <Grid key={v.id} item xs={12}>
                        <Card variant="outlined">
                            <CardContent>
                                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                                    <Stack>
                                        <Typography variant="h6" sx={{fontWeight: 800}}>{v.title}</Typography>
                                        <Typography color="text.secondary">{v.minutes} min</Typography>
                                    </Stack>
                                    <Button variant="contained" endIcon={<PlayCircleOutlineIcon/>}
                                            component={RouterLink} to={`/academy/watch/${v.id}`}>
                                        Watch
                                    </Button>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Container>
    );
}

// =========================
// VIDEO PLAYER (token-gated)
// =========================
export function AcademyPlayer({id}) {
    const token = useAuthToken();
    const navigate = useNavigate();
    useEffect(() => {
        if (!token) navigate('/login');
    }, [token, navigate]);

    // Replace with real source fetched from API once authenticated
    const src = '';

    return (
        <Container maxWidth="md" sx={{py: 6}}>
            <Typography variant="h5" sx={{fontWeight: 800, mb: 2}}>Lesson: {id}</Typography>
            <Box sx={{
                aspectRatio: '16/9',
                width: '100%',
                borderRadius: 2,
                overflow: 'hidden',
                border: (t) => `1px solid ${t.palette.divider}`
            }}>
                {/* Example: <video controls src={src} style={{ width: '100%', height: '100%' }} /> */}
                <Box sx={{display: 'grid', placeItems: 'center', height: '100%', color: 'text.secondary'}}>Connect your
                    protected video URL</Box>
            </Box>
            <Button sx={{mt: 2}} component={RouterLink} to="/academy" variant="text">Back to Academy</Button>
        </Container>
    );
}


/**
 * CONTACT PAGE — /contact
 * - Posts to /api/contact (JSON)
 * - Shows success/error feedback
 * - Includes a hidden honeypot field (bot trap)
 */
export function ContactPage() {
    const {t} = useTranslation();

    const [values, setValues] = useState({
        fullName: '',
        email: '',
        company: '',
        message: '',
        honeypot: '', // should stay empty
    });
    const [submitting, setSubmitting] = useState(false);
    const [notice, setNotice] = useState({open: false, type: 'success', text: ''});

    const onChange = (e) => {
        const {name, value} = e.target;
        setValues(v => ({...v, [name]: value}));
    };

    const validate = () => {
        const isWorkEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email);
        return values.fullName.trim() && isWorkEmail && values.message.trim();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (values.honeypot) return; // bot submission, silently ignore
        if (!validate()) {
            setNotice({open: true, type: 'error', text: t('contact.notices.invalid')});
            return;
        }
        setSubmitting(true);
        try {
            const res = await fetch(`${API_BASE}/contact`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({
                    fullName: values.fullName,
                    email: values.email,
                    company: values.company,
                    message: values.message,
                }),
            });
            if (!res.ok) throw new Error('Request failed');
            setValues({fullName: '', email: '', company: '', message: '', honeypot: ''});
            setNotice({open: true, type: 'success', text: t('contact.notices.success')});
        } catch (err) {
            setNotice({open: true, type: 'error', text: t('contact.notices.error')});
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Container maxWidth="sm" sx={{py: {xs: 6, md: 8}}}>
            <Typography variant="h4" sx={{fontWeight: 900, mb: 1}}>
                {t('contact.title')}
            </Typography>
            <Typography color="text.secondary" sx={{mb: 4}}>
                {t('contact.intro')}
            </Typography>
            <Box component="form" onSubmit={handleSubmit} noValidate>
                {/* Honeypot (hidden) */}
                <TextField
                    label={t('contact.fields.honeypot')}
                    name="honeypot"
                    value={values.honeypot}
                    onChange={onChange}
                    autoComplete="off"
                    sx={{display: 'none'}}
                />
                <Stack spacing={2}>
                    <TextField
                        required
                        label={t('contact.fields.fullName')}
                        name="fullName"
                        value={values.fullName}
                        onChange={onChange}
                        autoComplete="name"
                        fullWidth
                    />
                    <TextField
                        required
                        type="email"
                        label={t('contact.fields.email')}
                        name="email"
                        value={values.email}
                        onChange={onChange}
                        autoComplete="email"
                        fullWidth
                        helperText={t('contact.fields.emailHelper')}
                    />
                    <TextField
                        label={t('contact.fields.company')}
                        name="company"
                        value={values.company}
                        onChange={onChange}
                        autoComplete="organization"
                        fullWidth
                    />
                    <TextField
                        required
                        label={t('contact.fields.message')}
                        name="message"
                        value={values.message}
                        onChange={onChange}
                        fullWidth
                        multiline
                        minRows={5}
                    />
                    <Stack direction="row" spacing={1}>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={submitting}
                            endIcon={<ArrowForwardIcon/>}
                        >
                            {submitting ? t('contact.actions.submitting') : t('contact.actions.submit')}
                        </Button>
                    </Stack>
                </Stack>
            </Box>
            <Snackbar
                open={notice.open}
                autoHideDuration={5000}
                onClose={() => setNotice(n => ({...n, open: false}))}
                anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
            >
                <Alert
                    onClose={() => setNotice(n => ({...n, open: false}))}
                    severity={notice.type}
                    variant="filled"
                    sx={{width: '100%'}}
                >
                    {notice.text}
                </Alert>
            </Snackbar>
        </Container>
    );
}


// =========================
// ROUTING HOOKS (v6) — add these to App.js
// =========================
// 1) Import pages:
// import LandingPage, { AcademyPage } from './components/Landing/LandingPage';
// import AcademyWatchRoute from './components/Landing/AcademyWatchRoute';
// 2) Replace your "/" route to show LandingPage (move Login to "/login"):
// <Route path="/" element={<LandingPage />} />
// <Route path="/login" element={<LoginPage setToken={setToken} />} />
// <Route path="/academy" element={<AcademyPage />} />
// <Route path="/academy/watch/:id" element={<AcademyWatchRoute />} />

// =========================
// SMALL ROUTE ADAPTER for :id param -> AcademyPlayer
// =========================
export function AcademyWatchRoute() {
    const id = window.location.pathname.split('/').pop();
    return <AcademyPlayer id={id}/>;
}



